import { useId, useRef, useState, type FormEvent } from 'react'
import { Activity, Info, RotateCcw, Scale } from 'lucide-react'
import './BmiCalculator.css'

type UnitSystem = 'metric' | 'imperial'
type FieldName = 'heightCm' | 'weightKg' | 'heightFt' | 'heightIn' | 'weightLb'
type FormValues = Record<FieldName, string>
type ValidationErrors = Partial<Record<FieldName, string>>

type BmiCategory = {
  label: string
  summary: string
  tone: 'under' | 'reference' | 'over' | 'obesity'
}

type BmiResult = BmiCategory & {
  value: number
}

const initialValues: FormValues = {
  heightCm: '',
  weightKg: '',
  heightFt: '',
  heightIn: '',
  weightLb: '',
}

function toNumber(value: string) {
  const parsed = Number(value)
  return value.trim() !== '' && Number.isFinite(parsed) ? parsed : null
}

function getCategory(bmi: number): BmiCategory {
  if (bmi < 18.5) {
    return {
      label: 'Underweight range',
      summary: 'Below the common adult reference range.',
      tone: 'under',
    }
  }

  if (bmi < 25) {
    return {
      label: 'Reference range',
      summary: 'Within the common adult reference range.',
      tone: 'reference',
    }
  }

  if (bmi < 30) {
    return {
      label: 'Overweight range',
      summary: 'Above the common adult reference range.',
      tone: 'over',
    }
  }

  return {
    label: 'Obesity range',
    summary: 'Within the common adult obesity screening range.',
    tone: 'obesity',
  }
}

function calculateMetricBmi(values: FormValues) {
  const errors: ValidationErrors = {}
  const heightCm = toNumber(values.heightCm)
  const weightKg = toNumber(values.weightKg)

  if (heightCm === null) errors.heightCm = 'Enter your height in centimetres.'
  else if (heightCm < 100 || heightCm > 250) errors.heightCm = 'Enter a height between 100 and 250 cm.'

  if (weightKg === null) errors.weightKg = 'Enter your weight in kilograms.'
  else if (weightKg < 25 || weightKg > 400) errors.weightKg = 'Enter a weight between 25 and 400 kg.'

  if (Object.keys(errors).length > 0 || heightCm === null || weightKg === null) {
    return { errors, bmi: null }
  }

  const heightMetres = heightCm / 100
  return { errors, bmi: weightKg / heightMetres ** 2 }
}

function calculateImperialBmi(values: FormValues) {
  const errors: ValidationErrors = {}
  const feet = toNumber(values.heightFt)
  const inches = toNumber(values.heightIn)
  const weightLb = toNumber(values.weightLb)

  if (feet === null) errors.heightFt = 'Enter the feet portion of your height.'
  else if (!Number.isInteger(feet) || feet < 3 || feet > 8) errors.heightFt = 'Enter whole feet between 3 and 8.'

  if (inches === null) errors.heightIn = 'Enter the inches portion of your height, using 0 if needed.'
  else if (inches < 0 || inches >= 12) errors.heightIn = 'Enter inches from 0 up to 11.9.'

  if (weightLb === null) errors.weightLb = 'Enter your weight in pounds.'
  else if (weightLb < 55 || weightLb > 880) errors.weightLb = 'Enter a weight between 55 and 880 lb.'

  if (feet !== null && inches !== null) {
    const totalInches = feet * 12 + inches
    if (totalInches < 40 || totalInches > 98) {
      errors.heightFt = 'Enter a total height between 3 ft 4 in and 8 ft 2 in.'
    }
  }

  if (Object.keys(errors).length > 0 || feet === null || inches === null || weightLb === null) {
    return { errors, bmi: null }
  }

  const totalInches = feet * 12 + inches
  return { errors, bmi: (703 * weightLb) / totalInches ** 2 }
}

export function BmiCalculator() {
  const id = useId()
  const [unitSystem, setUnitSystem] = useState<UnitSystem>('metric')
  const [values, setValues] = useState<FormValues>(initialValues)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [result, setResult] = useState<BmiResult | null>(null)
  const inputRefs = useRef<Partial<Record<FieldName, HTMLInputElement>>>({})

  const fieldId = (name: FieldName) => `${id}-${name}`
  const errorId = (name: FieldName) => `${fieldId(name)}-error`

  const updateValue = (name: FieldName, value: string) => {
    setValues((current) => ({ ...current, [name]: value }))
    setErrors((current) => {
      if (!current[name]) return current
      const next = { ...current }
      delete next[name]
      return next
    })
    setResult(null)
  }

  const selectUnits = (nextUnitSystem: UnitSystem) => {
    if (nextUnitSystem === unitSystem) return
    setUnitSystem(nextUnitSystem)
    setErrors({})
    setResult(null)
  }

  const resetCalculator = () => {
    setValues(initialValues)
    setErrors({})
    setResult(null)
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const calculation = unitSystem === 'metric'
      ? calculateMetricBmi(values)
      : calculateImperialBmi(values)

    if (calculation.bmi === null) {
      setErrors(calculation.errors)
      setResult(null)
      const firstInvalidField = Object.keys(calculation.errors)[0] as FieldName | undefined
      if (firstInvalidField) inputRefs.current[firstInvalidField]?.focus()
      return
    }

    const roundedBmi = Math.round(calculation.bmi * 10) / 10
    setErrors({})
    setResult({ value: roundedBmi, ...getCategory(roundedBmi) })
  }

  const markerPosition = result
    ? Math.min(100, Math.max(0, ((result.value - 14) / 26) * 100))
    : 0

  const resultInputIds = unitSystem === 'metric'
    ? `${fieldId('heightCm')} ${fieldId('weightKg')}`
    : `${fieldId('heightFt')} ${fieldId('heightIn')} ${fieldId('weightLb')}`

  return (
    <section className="bmi-calculator" aria-labelledby={`${id}-title`}>
      <header className="bmi-calculator__header">
        <span className="bmi-calculator__icon" aria-hidden="true"><Scale size={24} /></span>
        <div>
          <span className="eyebrow">Everyday health tool</span>
          <h2 id={`${id}-title`}>Adult BMI calculator</h2>
          <p>Estimate body mass index from your height and weight. Your entries stay in this browser and are not submitted.</p>
        </div>
      </header>

      <div className="bmi-calculator__layout">
        <form className="bmi-calculator__form" onSubmit={handleSubmit} noValidate>
          <fieldset className="bmi-calculator__unit-fieldset">
            <legend>Choose a unit system</legend>
            <div className="bmi-calculator__unit-toggle">
              <label className={unitSystem === 'metric' ? 'is-active' : undefined}>
                <input
                  type="radio"
                  name={`${id}-units`}
                  value="metric"
                  checked={unitSystem === 'metric'}
                  onChange={() => selectUnits('metric')}
                />
                Metric <small>cm · kg</small>
              </label>
              <label className={unitSystem === 'imperial' ? 'is-active' : undefined}>
                <input
                  type="radio"
                  name={`${id}-units`}
                  value="imperial"
                  checked={unitSystem === 'imperial'}
                  onChange={() => selectUnits('imperial')}
                />
                Imperial <small>ft · in · lb</small>
              </label>
            </div>
          </fieldset>

          {Object.keys(errors).length > 0 && (
            <p className="bmi-calculator__form-alert" role="alert">Please review the highlighted {Object.keys(errors).length === 1 ? 'field' : 'fields'}.</p>
          )}

          {unitSystem === 'metric' ? (
            <div className="bmi-calculator__fields">
              <label className="bmi-calculator__field" htmlFor={fieldId('heightCm')}>
                <span>Height <small>centimetres</small></span>
                <div className="bmi-calculator__input-wrap">
                  <input
                    ref={(node) => { inputRefs.current.heightCm = node ?? undefined }}
                    id={fieldId('heightCm')}
                    type="number"
                    inputMode="decimal"
                    min="100"
                    max="250"
                    step="0.1"
                    value={values.heightCm}
                    onChange={(event) => updateValue('heightCm', event.target.value)}
                    aria-invalid={Boolean(errors.heightCm)}
                    aria-describedby={errors.heightCm ? errorId('heightCm') : undefined}
                    required
                  />
                  <span aria-hidden="true">cm</span>
                </div>
                {errors.heightCm && <span className="bmi-calculator__field-error" id={errorId('heightCm')}>{errors.heightCm}</span>}
              </label>

              <label className="bmi-calculator__field" htmlFor={fieldId('weightKg')}>
                <span>Weight <small>kilograms</small></span>
                <div className="bmi-calculator__input-wrap">
                  <input
                    ref={(node) => { inputRefs.current.weightKg = node ?? undefined }}
                    id={fieldId('weightKg')}
                    type="number"
                    inputMode="decimal"
                    min="25"
                    max="400"
                    step="0.1"
                    value={values.weightKg}
                    onChange={(event) => updateValue('weightKg', event.target.value)}
                    aria-invalid={Boolean(errors.weightKg)}
                    aria-describedby={errors.weightKg ? errorId('weightKg') : undefined}
                    required
                  />
                  <span aria-hidden="true">kg</span>
                </div>
                {errors.weightKg && <span className="bmi-calculator__field-error" id={errorId('weightKg')}>{errors.weightKg}</span>}
              </label>
            </div>
          ) : (
            <div className="bmi-calculator__imperial-fields">
              <fieldset className="bmi-calculator__height-group">
                <legend>Height</legend>
                <div className="bmi-calculator__height-inputs">
                  <label className="bmi-calculator__field" htmlFor={fieldId('heightFt')}>
                    <span>Feet</span>
                    <div className="bmi-calculator__input-wrap">
                      <input
                        ref={(node) => { inputRefs.current.heightFt = node ?? undefined }}
                        id={fieldId('heightFt')}
                        type="number"
                        inputMode="numeric"
                        min="3"
                        max="8"
                        step="1"
                        value={values.heightFt}
                        onChange={(event) => updateValue('heightFt', event.target.value)}
                        aria-invalid={Boolean(errors.heightFt)}
                        aria-describedby={errors.heightFt ? errorId('heightFt') : undefined}
                        required
                      />
                      <span aria-hidden="true">ft</span>
                    </div>
                    {errors.heightFt && <span className="bmi-calculator__field-error" id={errorId('heightFt')}>{errors.heightFt}</span>}
                  </label>

                  <label className="bmi-calculator__field" htmlFor={fieldId('heightIn')}>
                    <span>Inches</span>
                    <div className="bmi-calculator__input-wrap">
                      <input
                        ref={(node) => { inputRefs.current.heightIn = node ?? undefined }}
                        id={fieldId('heightIn')}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max="11.9"
                        step="0.1"
                        value={values.heightIn}
                        onChange={(event) => updateValue('heightIn', event.target.value)}
                        aria-invalid={Boolean(errors.heightIn)}
                        aria-describedby={errors.heightIn ? errorId('heightIn') : undefined}
                        required
                      />
                      <span aria-hidden="true">in</span>
                    </div>
                    {errors.heightIn && <span className="bmi-calculator__field-error" id={errorId('heightIn')}>{errors.heightIn}</span>}
                  </label>
                </div>
              </fieldset>

              <label className="bmi-calculator__field" htmlFor={fieldId('weightLb')}>
                <span>Weight <small>pounds</small></span>
                <div className="bmi-calculator__input-wrap">
                  <input
                    ref={(node) => { inputRefs.current.weightLb = node ?? undefined }}
                    id={fieldId('weightLb')}
                    type="number"
                    inputMode="decimal"
                    min="55"
                    max="880"
                    step="0.1"
                    value={values.weightLb}
                    onChange={(event) => updateValue('weightLb', event.target.value)}
                    aria-invalid={Boolean(errors.weightLb)}
                    aria-describedby={errors.weightLb ? errorId('weightLb') : undefined}
                    required
                  />
                  <span aria-hidden="true">lb</span>
                </div>
                {errors.weightLb && <span className="bmi-calculator__field-error" id={errorId('weightLb')}>{errors.weightLb}</span>}
              </label>
            </div>
          )}

          <div className="bmi-calculator__actions">
            <button className="button bmi-calculator__submit" type="submit">
              <Activity size={18} aria-hidden="true" /> Calculate BMI
            </button>
            <button className="bmi-calculator__reset" type="button" onClick={resetCalculator}>
              <RotateCcw size={15} aria-hidden="true" /> Reset
            </button>
          </div>
        </form>

        <div className="bmi-calculator__result-panel">
          {result ? (
            <div className={`bmi-calculator__result is-${result.tone}`} role="status" aria-live="polite" aria-atomic="true">
              <span className="bmi-calculator__result-kicker">Your estimated BMI</span>
              <output className="bmi-calculator__result-value" htmlFor={resultInputIds}>{result.value.toFixed(1)}</output>
              <strong>{result.label}</strong>
              <p>{result.summary}</p>
              <div className="bmi-calculator__meter" aria-hidden="true">
                <span className="is-under" />
                <span className="is-reference" />
                <span className="is-over" />
                <span className="is-obesity" />
                <i style={{ left: `${markerPosition}%` }} />
              </div>
              <div className="bmi-calculator__scale" aria-hidden="true"><span>14</span><span>18.5</span><span>25</span><span>30</span><span>40+</span></div>
            </div>
          ) : (
            <div className="bmi-calculator__empty-result" aria-live="polite">
              <span aria-hidden="true"><Activity size={28} /></span>
              <h3>Your estimate will appear here</h3>
              <p>Enter your measurements, then choose “Calculate BMI” to see the result and adult screening category.</p>
            </div>
          )}

          <div className="bmi-calculator__guide" aria-label="Common adult BMI reference ranges">
            <span><i className="is-under" />Under 18.5</span>
            <span><i className="is-reference" />18.5–24.9</span>
            <span><i className="is-over" />25.0–29.9</span>
            <span><i className="is-obesity" />30 or above</span>
          </div>
        </div>
      </div>

      <aside className="bmi-calculator__notice">
        <Info size={19} aria-hidden="true" />
        <p><strong>BMI is a screening measure, not a diagnosis.</strong> It does not directly measure body fat or account for factors such as muscle mass, body composition, ethnicity, or individual health circumstances. It is not intended for people under 20 or during pregnancy. Discuss weight or health concerns with a qualified clinician.</p>
      </aside>
    </section>
  )
}
